import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Loading, Panel, TimeFrameInput, Toggle, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazyGetOffloadingConfigurationQuery, useUpdateOffloadingConfigurationMutation } from "@/redux/apis/addOn/addOnApi";
import { useAppSelector } from "@/redux/hooks";
import { addOnItemSelector } from "@/redux/slices/addOn/selectors";
import { OffloadingConfiguration } from "@/redux/slices/addOn/types";
import { addToast } from "@/utils";
import { offloadingFormSchema } from "./offloadingSchema";

export const OffloadingEditorInDispatch = () => {
  const navigate = useNavigate();
  const offloading = useAppSelector(addOnItemSelector("offloading"));
  const configuration = offloading?.configuration as OffloadingConfiguration | undefined;
  const [getOffloadingConfiguration, { isFetching }] = useLazyGetOffloadingConfigurationQuery();
  const [updateOffloadingConfiguration, { isLoading: isUpdating }] = useUpdateOffloadingConfigurationMutation();

  const {
    control,
    trigger,
    watch,
    register,
    reset,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<OffloadingConfiguration>({
    resolver: zodResolver(offloadingFormSchema),
    defaultValues: configuration,
  });

  const { enabledAutomaticDispatchPrivateNetwork, enabledAutomaticDispatchPublicNetwork } = watch();

  useEffect(() => {
    getOffloadingConfiguration();
  }, [getOffloadingConfiguration]);

  useEffect(() => {
    if (configuration) {
      reset(configuration);
    }
  }, [configuration, reset]);

  const onSubmit = handleSubmit((data) => {
    const {
      enabledAutomaticDispatchPrivateNetwork,
      dispatchAutoPrivateNetworkTime,
      dispatchAutoPrivateNetworkTimeframe,
      enabledAutomaticDispatchPublicNetwork,
      dispatchAutoPublicNetworkTime,
      dispatchAutoPublicNetworkTimeframe,
    } = data;

    updateOffloadingConfiguration({
      enabled_automatic_dispatch_private_network: enabledAutomaticDispatchPrivateNetwork,
      dispatch_auto_private_network_time: dispatchAutoPrivateNetworkTime,
      dispatch_auto_private_network_timeframe: dispatchAutoPrivateNetworkTimeframe,
      enabled_automatic_dispatch_public_network: enabledAutomaticDispatchPublicNetwork,
      dispatch_auto_public_network_time: dispatchAutoPublicNetworkTime,
      dispatch_auto_public_network_timeframe: dispatchAutoPublicNetworkTimeframe,
    })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Updated Offloading");
        navigate("..");
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  });

  if (!offloading) return null;

  return (
    <form onSubmit={onSubmit}>
      <Panel className="mt-5">
        <div className="relative">
          {(isFetching || isUpdating) && <Loading />}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Typography variant="h3" className="leading-8">
                Offloading
              </Typography>
              <Typography className="text-neutral-dark-gray">
                Automatically dispatch jobs to your private or public network when needed.
              </Typography>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => navigate("..")}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDirty || isUpdating}>
                Save
              </Button>
            </div>
          </div>

          <div className="my-4 flex w-full flex-col rounded-lg bg-neutral-surface-gray p-5">
            <div className="flex flex-row items-center">
              <div className="flex flex-1 flex-col">
                <Typography variant="action">Auto Dispatch to Private Network</Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set the time before pickup
                </Typography>
              </div>
              <Controller
                name="enabledAutomaticDispatchPrivateNetwork"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                      trigger();
                    }}
                  />
                )}
              />
            </div>
            <div className="flex flex-col">
              <div className="mt-5 flex flex-col lg:flex-row lg:items-center">
                <div className="flex flex-1 flex-row items-center gap-3">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Standard Hold Time
                  </Typography>
                </div>
                <TimeFrameInput
                  className="w-48"
                  min="0"
                  max="60"
                  timeName="dispatchAutoPrivateNetworkTime"
                  timeFrameName="dispatchAutoPrivateNetworkTimeframe"
                  register={register}
                  options={["hour", "minute", "day"]}
                  disabled={!enabledAutomaticDispatchPrivateNetwork}
                />
              </div>
              {errors.dispatchAutoPrivateNetworkTime?.message && (
                <Typography variant="paragraph" className="text-danger">
                  {errors.dispatchAutoPrivateNetworkTime.message}
                </Typography>
              )}
            </div>
            <Divider className="my-6" />
            <div className="flex flex-row items-center">
              <div className="flex flex-1 flex-col">
                <Typography variant="action">Auto Dispatch to Public Network</Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Set the time before pickup
                </Typography>
              </div>
              <Controller
                name="enabledAutomaticDispatchPublicNetwork"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                      trigger();
                    }}
                  />
                )}
              />
            </div>
            <div className="flex flex-col">
              <div className="mt-5 flex flex-col lg:flex-row lg:items-center">
                <div className="flex flex-1 flex-row items-center gap-3">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Standard Hold Time
                  </Typography>
                </div>
                <TimeFrameInput
                  className="w-48"
                  min="0"
                  max="60"
                  timeName="dispatchAutoPublicNetworkTime"
                  timeFrameName="dispatchAutoPublicNetworkTimeframe"
                  register={register}
                  options={["hour", "minute", "day"]}
                  disabled={!enabledAutomaticDispatchPublicNetwork}
                />
              </div>
              {errors.dispatchAutoPublicNetworkTime?.message && (
                <Typography variant="paragraph" className="text-danger">
                  {errors.dispatchAutoPublicNetworkTime.message}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </Panel>
    </form>
  );
};
